import axiosInstance from './AxiosConfig';

/**
 * Crear un nuevo torneo.
 *
 * @param {Object} tournamentData - Datos del torneo a crear.
 * @returns {Promise<Object>} - Respuesta del servidor con los datos del torneo creado.
 * @throws {Error} - Lanza un error si la solicitud falla.
 */
export const createTournamentAPI = async (tournamentData) => {
  try {
    const response = await axiosInstance.post('/api/torneos', tournamentData);
    //console.log(response.data, "service")
    return response.data;
  } catch (error) {
    console.error('Error al crear el torneo:', error);
    throw error;
  }
};

/**
 * Obtiene los detalles de un TorneoCategoria por su ID.
 * @param {number|string} idTorneoCategoria
 * @returns {Promise<Object>}
 */
export const fetchTorneoCategoriaAPI = async (idTorneoCategoria) => {
  try {
    const resp = await axiosInstance.get(
      `/api/torneos/torneoCategoria/${idTorneoCategoria}`
    );
    // console.log('TorneoCategoria:', resp.data);
    return resp.data;
  } catch (error) {
    console.error('Error al obtener TorneoCategoria:', error);
    throw error;
  }
};

// services/torneoService.js
/**
 * Obtiene el resumen de torneos de una federación
 * @param {number|string} federationId
 */
export const fetchResumenTorneosByFederationAPI = async (federationId) => {
  const resp = await axiosInstance.get(`/api/torneos/federacion/${federationId}/resumen`);
  return resp.data; // [{ id_torneo, modalidad, cantidad_categorias }, …]
};

/**
 * Obtener todos los partidos de un torneo con toda su información.
 *
 * @param {number|string} torneoId - ID del torneo.
 * @returns {Promise<Array>} - Array de partidos.
 * @throws {Error} - Si la petición falla.
 */
export const fetchTournamentMatchesAPI = async (torneoId) => {
  try {
    const response = await axiosInstance.get(`/api/partidos/torneo/${torneoId}`);
    return response.data; // viene el array de partidos completos
  } catch (error) {
    console.error('Error al obtener los partidos del torneo:', error);
    throw error;
  }
};

export const fetchCategoriesSummaryAPI = async (federationId, tournamentId) => {
  const resp = await axiosInstance.get(
    `/api/torneos/federacion/${federationId}/torneo/${tournamentId}/torneocategorias-summary`
  );
  return resp.data;  // { categorias: [ … ] }
};

/**
 * Agregar categorías a un torneo existente.
 *
 * @param {number|string} torneoId - ID del torneo.
 * @param {Object} categoryConfig - Configuración de las categorías a agregar.
 * @returns {Promise<Object>} - Respuesta del servidor.
 * @throws {Error} - Lanza un error si la solicitud falla.
 */
export const addCategoryToTournamentAPI = async (torneoId, categoryConfig) => {
  try {
    const response = await axiosInstance.post(`/api/torneos/${torneoId}/add-category`, { categoryConfig });
    return response.data;
  } catch (error) {
    console.error('Error al agregar categorías al torneo:', error);
    throw error;
  }
};

/**
 * Eliminar una categoría de un torneo existente.
 *
 * @param {number|string} idTorneoCategoria - ID de la relación TorneoCategoria a eliminar.
 * @returns {Promise<Object>} - Torneo actualizado después de la eliminación.
 * @throws {Error} - Lanza un error si la solicitud falla.
 */
export const removeCategoryFromTournamentAPI = async (idTorneoCategoria) => {
  try {
    const response = await axiosInstance.delete(`/api/torneos/${idTorneoCategoria}/remove-category`);
    return response.data; // Retorna el torneo actualizado
  } catch (error) {
    console.error('Error al eliminar la categoría del torneo:', error);
    throw error;
  }
};


/**
 * Obtener todos los torneos.
 *
 * @returns {Promise<Array>} - Lista de torneos.
 * @throws {Error} - Lanza un error si la solicitud falla.
 */
export const fetchTournamentsAPI = async () => {
  try {
    const response = await axiosInstance.get('/api/torneos');
    return response.data;
  } catch (error) {
    console.error('Error al obtener los torneos:', error);
    throw error;
  }
};

/**
 * Obtener un torneo específico por ID.
 *
 * @param {number|string} torneoId - ID del torneo.
 * @returns {Promise<Object>} - Detalles del torneo.
 * @throws {Error} - Lanza un error si la solicitud falla.
 */
export const fetchTournamentByIdAPI = async (torneoId) => {
  try {
    const response = await axiosInstance.get(`/api/torneos/${torneoId}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener el torneo:', error);
    throw error;
  }
};

export const fetchTournamentByIdFederationAPI = async (federationId) => {
  try {
    const response = await axiosInstance.get(`/api/torneos/federacion/${federationId}`);
    //console.log('response.data', response.data);
    return response.data;
  } catch (error) {
    console.error('Error al obtener el torneo:', error);
    throw error;
  }
};

/**
 * Obtener torneos por federación y formato.
 *
 * @param {number|string} federationId - ID de la federación.
 * @param {string} formato - Formato del torneo (por ejemplo, "Grand-Prix").
 * @returns {Promise<Array>} - Lista de torneos que cumplen con los criterios.
 * @throws {Error} - Lanza un error si la solicitud falla.
 */
export const fetchTournamentsForCreationAPI = fetchTournamentByIdFederationAPI;

export const fetchTournamentsByFederationAndFormatAPI = async (federationId, formato) => {
  try {
    const response = await axiosInstance.get(`/api/torneos/federacion/${federationId}/formatoTorneo`, {
      params: { formato },
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener los torneos por formato:', error);
    throw error;
  }
};

/**
 * Obtener torneos completados por ID de federación.
 *
 * @param {number|string} federationId - ID de la federación.
 * @returns {Promise<Array>} - Lista de torneos completados.
 * @throws {Error} - Lanza un error si la solicitud falla.
 */
export const fetchCompletedTournamentsByFederationAPI = async (federationId) => {
  try {
    const response = await axiosInstance.get(`/api/torneos/federacion/${federationId}/torneos-con-categorias-completadas`);
    // console.log('Torneos completados:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error al obtener los torneos completados:', error);
    throw error;
  }
};

/**
 * Obtener categorias completadas por ID de torneo.
 *
 * @param {number|string} federationId  - ID de la federación.
 * @param {number|string} torneoId  - ID de la federación.
 * @returns {Promise<Array>} - Lista de torneos completados.
 * @throws {Error} - Lanza un error si la solicitud falla.
 */
export const fetchCompletedCategoriesByTournamentAPI = async (federationId,torneoId) => {
  try {
    const response = await axiosInstance.get(`/api/torneos/federacion/${federationId}/torneo/${torneoId}/torneocategorias`);
    // console.log('Torneos completados:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error al obtener los torneos completados:', error);
    throw error;
  }
};

/**
 * Actualizar un torneo existente.
 *
 * @param {number|string} torneoId - ID del torneo a actualizar.
 * @param {Object} updateData - Datos a actualizar del torneo.
 * @returns {Promise<Object>} - Respuesta del servidor con los datos del torneo actualizado.
 * @throws {Error} - Lanza un error si la solicitud falla.
 */
export const updateTournamentAPI = async (torneoId, updateData) => {
  try {
    const response = await axiosInstance.put(`/api/torneos/${torneoId}`, updateData);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar el torneo:', error);
    throw error;
  }
};

// services/torneoService.js
export const deleteTournamentAPI = async (idTorneo) => {
  try {
    const response = await axiosInstance.delete(`/api/torneos/${idTorneo}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar el torneo:', error);
    throw error;
  }
};
